/* Capacitaciones Administrativas - alta/edición (anfitrión) (TD-11629).
 *
 * Se puede guardar como BORRADOR (para tener el tema preparado: solo exige el
 * tema y no lo ven los participantes) o PUBLICAR (exige fecha, hora y duración:
 * la evaluación se habilita recién al terminar la capacitación). El tema es libre. Los departamentos son informativos (para reportes): no
 * restringen quién puede participar. Las preguntas de la evaluación se pueden
 * editar hasta que alguien responde; a partir de ahí quedan fijas.
 */
import { IonButton, IonContent, IonIcon, IonPage, useIonToast } from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { addOutline, arrowBack, checkmark, lockClosedOutline, trashOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "src/components/Header";
import CapacitacionAdmAPI, {
  CapacitacionForm,
  EstadoCapacitacion,
  Modalidad,
  OpcionForm,
  PreguntaForm,
} from "src/services/capacitacionAdm";
import "./CapacitacionAdm.css";

const MAX_PREGUNTAS = 20;
const MAX_OPCIONES = 6;

// Claves estables para las listas editables: con el índice como key, al borrar
// una pregunta u opción React reutiliza el input del siguiente elemento y el
// texto "salta" a otro campo.
let ultimaClave = 0;
const nuevaClave = () => ++ultimaClave;

const nuevaOpcion = (es_correcta = false): OpcionForm => ({ _k: nuevaClave(), texto: "", es_correcta });

const nuevaPregunta = (): PreguntaForm => ({
  _k: nuevaClave(),
  texto: "",
  opciones: [nuevaOpcion(true), nuevaOpcion()],
});

const conClaves = (preguntas: PreguntaForm[]): PreguntaForm[] =>
  preguntas.map((p) => ({
    ...p,
    _k: nuevaClave(),
    opciones: (p.opciones || []).map((o) => ({ ...o, _k: nuevaClave() })),
  }));

const DURACION_SUGERIDA = "60";

// 'YYYY-MM-DD HH:MM:SS' (backend) -> ['YYYY-MM-DD', 'HH:MM'] (inputs date / time)
const separarFecha = (valor?: string | null): [string, string] =>
  valor ? [valor.slice(0, 10), valor.slice(11, 16)] : ["", ""];

// Hoy (fecha local) para el mínimo del selector.
const hoyLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const CapacitacionAdmForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const capacitacionId = id ? Number(id) : null;
  const history = useHistory();
  const [present] = useIonToast();

  const [cargando, setCargando] = useState(Boolean(capacitacionId));
  const [guardando, setGuardando] = useState(false);
  const [departamentosDisponibles, setDepartamentosDisponibles] = useState<string[]>([]);
  const [evaluacionIniciada, setEvaluacionIniciada] = useState(false);
  // Estado actual (null = capacitación nueva, todavía sin guardar).
  const [estado, setEstado] = useState<EstadoCapacitacion | null>(null);

  const [tema, setTema] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("");
  const [duracion, setDuracion] = useState(capacitacionId ? "" : DURACION_SUGERIDA);
  const [modalidad, setModalidad] = useState<Modalidad>("PRESENCIAL");
  const [lugar, setLugar] = useState("");
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [preguntas, setPreguntas] = useState<PreguntaForm[]>([]);

  useEffect(() => {
    CapacitacionAdmAPI.departamentos()
      .then(setDepartamentosDisponibles)
      .catch(() => setDepartamentosDisponibles([]));

    if (!capacitacionId) return;
    (async () => {
      try {
        const cap = await CapacitacionAdmAPI.obtener(capacitacionId);
        if (cap.estado === "ANULADA") {
          present({ message: "La capacitación está anulada", duration: 3000, color: "warning" });
          history.replace(`/capacitaciones-adm/${capacitacionId}`);
          return;
        }
        setTema(cap.tema || "");
        setDescripcion(cap.descripcion || "");
        const [dia, hh] = separarFecha(cap.fecha_capacitacion);
        setFecha(dia);
        setHora(hh);
        setEstado(cap.estado);
        setDuracion(cap.duracion_minutos ? String(cap.duracion_minutos) : "");
        setModalidad(cap.modalidad);
        setLugar(cap.lugar || "");
        setDepartamentos(cap.departamentos || []);
        setPreguntas(conClaves(cap.preguntas || []));
        setEvaluacionIniciada(Boolean(cap.evaluacion_iniciada));
      } catch (err: any) {
        present({ message: err?.response?.data?.error || "No se pudo cargar la capacitación", duration: 3500, color: "danger" });
        history.replace("/capacitaciones-adm");
      } finally {
        setCargando(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacitacionId]);

  // ------------------------- Editor de preguntas -------------------------
  // Todas las ediciones usan el estado previo (no el del render) para no
  // perder cambios si se encadenan dos actualizaciones seguidas.
  const editarPregunta = (pi: number, cambio: (p: PreguntaForm) => PreguntaForm) =>
    setPreguntas((prev) => prev.map((p, i) => (i === pi ? cambio(p) : p)));

  const editarOpcion = (pi: number, oi: number, texto: string) =>
    editarPregunta(pi, (p) => ({ ...p, opciones: p.opciones.map((o, i) => (i === oi ? { ...o, texto } : o)) }));

  const marcarCorrecta = (pi: number, oi: number) =>
    editarPregunta(pi, (p) => ({ ...p, opciones: p.opciones.map((o, i) => ({ ...o, es_correcta: i === oi })) }));

  const agregarOpcion = (pi: number) =>
    editarPregunta(pi, (p) => ({ ...p, opciones: [...p.opciones, nuevaOpcion()] }));

  const quitarOpcion = (pi: number, oi: number) =>
    editarPregunta(pi, (p) => {
      const opciones = p.opciones.filter((_, i) => i !== oi);
      // Si se borró la correcta, la primera pasa a ser la correcta.
      if (!opciones.some((o) => o.es_correcta) && opciones.length > 0) {
        opciones[0] = { ...opciones[0], es_correcta: true };
      }
      return { ...p, opciones };
    });

  // ------------------------- Guardar -------------------------
  const esBorrador = estado === null || estado === "BORRADOR";

  /** @param completa true al publicar o al editar una ya publicada. */
  const validar = (completa: boolean): string | null => {
    if (!tema.trim()) return "Ingresá el tema";
    if (Boolean(fecha) !== Boolean(hora)) return "Completá la fecha y la hora (o dejá las dos vacías en el borrador)";
    if (duracion && Number(duracion) <= 0) return "La duración debe ser mayor a 0 minutos";
    if (completa) {
      if (!fecha || !hora) return "Para publicar indicá la fecha y la hora";
      if (!duracion) return "Para publicar indicá la duración: la evaluación se habilita al terminar";
    }
    if (!evaluacionIniciada) {
      for (let i = 0; i < preguntas.length; i++) {
        const p = preguntas[i];
        if (!p.texto.trim()) return `Completá el texto de la pregunta ${i + 1}`;
        const llenas = p.opciones.filter((o) => o.texto.trim());
        if (llenas.length < 2) return `La pregunta ${i + 1} necesita al menos 2 opciones`;
        if (!llenas.some((o) => o.es_correcta)) return `Marcá cuál es la opción correcta de la pregunta ${i + 1}`;
      }
    }
    return null;
  };

  /** @param publicar true = publicar (o guardar una ya publicada); false = guardar borrador. */
  const guardar = async (publicar: boolean) => {
    const error = validar(publicar || !esBorrador);
    if (error) {
      present({ message: error, duration: 3500, color: "warning" });
      return;
    }
    const form: CapacitacionForm = {
      tema: tema.trim(),
      descripcion: descripcion.trim(),
      fecha_capacitacion: fecha && hora ? `${fecha}T${hora}` : null,
      duracion_minutos: duracion ? Number(duracion) : null,
      modalidad,
      lugar: lugar.trim(),
      departamentos,
      publicar,
    };
    // Con la evaluación ya empezada no se mandan: el backend las rechazaría.
    if (!evaluacionIniciada) {
      form.preguntas = preguntas.map((p) => ({
        texto: p.texto.trim(),
        opciones: p.opciones
          .filter((o) => o.texto.trim())
          .map((o) => ({ texto: o.texto.trim(), es_correcta: o.es_correcta })),
      }));
    }

    setGuardando(true);
    try {
      if (capacitacionId) {
        const res = await CapacitacionAdmAPI.actualizar(capacitacionId, form);
        present({ message: res.info || "Cambios guardados", duration: 2500, color: "success" });
        history.replace(`/capacitaciones-adm/${capacitacionId}`);
      } else {
        const res = await CapacitacionAdmAPI.crear(form);
        present({ message: res.info || "Guardado", duration: 2500, color: "success" });
        history.replace(`/capacitaciones-adm/${res.id}`);
      }
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo guardar", duration: 4000, color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  const volverA = capacitacionId ? `/capacitaciones-adm/${capacitacionId}` : "/capacitaciones-adm";

  return (
    <IonPage>
      <Header titulo={capacitacionId ? "Editar capacitación" : "Nueva capacitación"} botonIzq={{ icono: arrowBack, url: volverA }} />
      <IonContent id="capacitaciones-adm-form">
        {cargando ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : (
          <div className="capadm-page capadm-scope campos-mui">
            {/* ---------- Datos ---------- */}
            <div className="capadm-card">
              <p className="capadm-card-titulo">Datos de la capacitación</p>
              <p className="capadm-card-sub">
                {esBorrador
                  ? "En borrador solo el tema es obligatorio. Para publicar hacen falta fecha, hora y duración."
                  : "El tema y la fecha aparecen en la declaración que acepta cada participante."}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <TextField label="Tema *" variant="outlined" fullWidth value={tema} inputProps={{ maxLength: 200 }}
                  onChange={(e) => setTema(e.target.value)} />
                <TextField label="Descripción" variant="outlined" fullWidth multiline minRows={2} value={descripcion}
                  inputProps={{ maxLength: 1000 }} onChange={(e) => setDescripcion(e.target.value)} />
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <TextField label={esBorrador ? "Fecha" : "Fecha *"} type="date" variant="outlined" value={fecha}
                    style={{ flex: "1 1 150px" }} InputLabelProps={{ shrink: true }}
                    inputProps={{ min: estado === null ? hoyLocal() : undefined }}
                    onChange={(e) => setFecha(e.target.value)} />
                  <TextField label={esBorrador ? "Hora" : "Hora *"} type="time" variant="outlined" value={hora}
                    style={{ flex: "1 1 110px" }} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
                    onChange={(e) => setHora(e.target.value)} />
                  <TextField label={esBorrador ? "Duración (min)" : "Duración (min) *"} variant="outlined" value={duracion}
                    style={{ flex: "1 1 110px" }} inputProps={{ inputMode: "numeric", maxLength: 4 }}
                    onChange={(e) => setDuracion(e.target.value.replace(/\D/g, ""))} />
                </div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 140px" }}>
                    <select aria-label="Modalidad" value={modalidad} className="capadm-select"
                      onChange={(e) => setModalidad(e.target.value as Modalidad)}>
                      <option value="PRESENCIAL">Presencial</option>
                      <option value="VIRTUAL">Virtual</option>
                    </select>
                  </div>
                  <TextField label={modalidad === "VIRTUAL" ? "Plataforma / enlace" : "Lugar"} variant="outlined"
                    style={{ flex: "2 1 200px" }} value={lugar} inputProps={{ maxLength: 200 }}
                    onChange={(e) => setLugar(e.target.value)} />
                </div>
                <MuiAutocomplete
                  multiple
                  options={departamentosDisponibles}
                  value={departamentos}
                  onChange={(_, val) => setDepartamentos(val)}
                  filterSelectedOptions
                  noOptionsText="Sin coincidencias"
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Dirigida a (departamentos)"
                      helperText="Informativo para reportes: cualquier funcionario puede participar." />
                  )}
                />
              </div>
            </div>

            {/* ---------- Evaluación ---------- */}
            <div className="capadm-card">
              <p className="capadm-card-titulo">Evaluación {preguntas.length > 0 ? `(${preguntas.length})` : ""}</p>
              {evaluacionIniciada ? (
                <div className="capadm-aviso info" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <IonIcon icon={lockClosedOutline} />
                  Ya hay participantes que respondieron: las preguntas quedaron fijas.
                </div>
              ) : (
                <p className="capadm-card-sub">
                  Opcional. Los participantes la reciben al confirmar su asistencia. Tocá el ✓ para marcar la opción correcta.
                </p>
              )}

              {preguntas.map((p, pi) => (
                <div key={p._k ?? pi} className="capadm-pregunta">
                  <div className="capadm-pregunta-header">
                    <span>Pregunta {pi + 1}</span>
                    {!evaluacionIniciada && (
                      <IonButton fill="clear" size="small" color="danger"
                        onClick={() => setPreguntas((prev) => prev.filter((_, i) => i !== pi))}>
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    )}
                  </div>
                  <TextField variant="outlined" fullWidth multiline value={p.texto} disabled={evaluacionIniciada}
                    placeholder="Escribí la pregunta" inputProps={{ maxLength: 500 }}
                    onChange={(e) => {
                      const texto = e.target.value;
                      editarPregunta(pi, (q) => ({ ...q, texto }));
                    }} />
                  <div style={{ marginTop: 10 }}>
                    {p.opciones.map((o, oi) => (
                      <div key={o._k ?? oi} className="capadm-opcion">
                        <button type="button" className={`capadm-correcta${o.es_correcta ? " activa" : ""}`}
                          disabled={evaluacionIniciada} onClick={() => marcarCorrecta(pi, oi)}
                          aria-label={o.es_correcta ? "Opción correcta" : "Marcar como correcta"}
                          title={o.es_correcta ? "Opción correcta" : "Marcar como correcta"}>
                          <IonIcon icon={checkmark} />
                        </button>
                        <TextField size="small" variant="outlined" fullWidth value={o.texto} disabled={evaluacionIniciada}
                          placeholder={`Opción ${oi + 1}`} inputProps={{ maxLength: 300 }}
                          onChange={(e) => editarOpcion(pi, oi, e.target.value)} />
                        {!evaluacionIniciada && p.opciones.length > 2 && (
                          <IonButton fill="clear" size="small" color="medium" onClick={() => quitarOpcion(pi, oi)}>
                            <IonIcon slot="icon-only" icon={trashOutline} />
                          </IonButton>
                        )}
                      </div>
                    ))}
                  </div>
                  {!evaluacionIniciada && p.opciones.length < MAX_OPCIONES && (
                    <IonButton fill="clear" size="small" onClick={() => agregarOpcion(pi)}>
                      <IonIcon slot="start" icon={addOutline} /> Agregar opción
                    </IonButton>
                  )}
                </div>
              ))}

              {!evaluacionIniciada && preguntas.length < MAX_PREGUNTAS && (
                <IonButton expand="block" fill="outline" onClick={() => setPreguntas((prev) => [...prev, nuevaPregunta()])}>
                  <IonIcon slot="start" icon={addOutline} /> Agregar pregunta
                </IonButton>
              )}
            </div>

            {esBorrador ? (
              <>
                <div className="capadm-aviso info">
                  <strong>Guardar borrador:</strong> queda preparada y solo la ves vos.{" "}
                  <strong>Publicar:</strong> habilita el QR y la carga de participantes.
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <IonButton fill="outline" style={{ flex: "1 1 160px" }} onClick={() => guardar(false)} disabled={guardando}>
                    {guardando ? "Guardando..." : "Guardar borrador"}
                  </IonButton>
                  <IonButton style={{ flex: "1 1 160px" }} onClick={() => guardar(true)} disabled={guardando}>
                    {guardando ? "Guardando..." : "Publicar"}
                  </IonButton>
                </div>
              </>
            ) : (
              <IonButton expand="block" onClick={() => guardar(true)} disabled={guardando}>
                {guardando ? "Guardando..." : "Guardar cambios"}
              </IonButton>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default CapacitacionAdmForm;
